import { useCallback, useEffect, useRef, useState } from 'react';

type ParentViewport = {
  visibleTop: number;
  visibleHeight: number;
};

const VIEWPORT_MARGIN = 20;

// État global pour persister les données du viewport parent entre les montages de composants
let globalParentViewport: ParentViewport | null = null;
let listenerCount = 0;

const handleMessage = (event: MessageEvent) => {
  try {
    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (data?.type === 'parent-viewport') {
      globalParentViewport = {
        visibleTop: data.visibleTop,
        visibleHeight: data.visibleHeight,
      };
    }
  } catch {
    // Ignore invalid messages
  }
};

/**
 * Hook pour écouter les événements postMessage du parent et maintenir l'état du viewport.
 * À utiliser au niveau de l'application (ex: _app.tsx) pour écouter en permanence.
 */
export const useParentViewportListener = (isIframe: boolean) => {
  useEffect(() => {
    if (!isIframe) return;

    // Ajouter le listener seulement si c'est le premier
    if (listenerCount === 0) {
      window.addEventListener('message', handleMessage);
    }
    listenerCount++;

    return () => {
      listenerCount--;
      if (listenerCount === 0) {
        window.removeEventListener('message', handleMessage);
      }
    };
  }, [isIframe]);
};

/**
 * Hook pour calculer la position de la modale dans le viewport visible du parent.
 * Retourne une ref à attacher au dialog et la position top calculée.
 */
export const useParentViewportPosition = (isIframe: boolean) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [positionTop, setPositionTop] = useState<number | undefined>(undefined);

  const calculatePosition = useCallback(() => {
    if (!isIframe || !globalParentViewport) {
      setPositionTop(undefined);
      return;
    }

    const dialogHeight = dialogRef.current?.offsetHeight ?? 0;
    if (dialogHeight === 0) {
      // La modale n'est pas encore rendue, on réessaie au prochain frame
      requestAnimationFrame(calculatePosition);
      return;
    }

    const { visibleTop, visibleHeight } = globalParentViewport;

    // Hauteur totale de l'iframe (contrainte absolue)
    const iframeHeight = document.documentElement.scrollHeight;

    // La modale ne peut pas dépasser le bas de l'iframe
    const maxTopIframe = iframeHeight - dialogHeight - VIEWPORT_MARGIN;

    // Limites basées sur la zone visible du parent
    const minTopVisible = visibleTop + VIEWPORT_MARGIN;
    const maxTopVisible = visibleTop + visibleHeight - dialogHeight - VIEWPORT_MARGIN;

    // Contrainte combinée: rester dans la zone visible ET dans l'iframe
    const minTop = Math.max(VIEWPORT_MARGIN, minTopVisible);
    const maxTop = Math.min(maxTopVisible, maxTopIframe);

    // Si la modale ne peut pas tenir entièrement dans la zone visible
    if (dialogHeight + 2 * VIEWPORT_MARGIN > visibleHeight || minTop > maxTop) {
      // Coller en haut de la zone visible, mais sans dépasser les limites de l'iframe
      const topPosition = Math.min(minTopVisible, maxTopIframe);
      setPositionTop(Math.max(VIEWPORT_MARGIN, topPosition));
      return;
    }

    // La modale tient dans la zone visible, on la centre
    const centeredTop = visibleTop + (visibleHeight - dialogHeight) / 2;
    setPositionTop(Math.max(minTop, Math.min(centeredTop, maxTop)));
  }, [isIframe]);

  // Recalculer quand le viewport change
  useEffect(() => {
    if (!isIframe) return;

    const handleViewportChange = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'parent-viewport') {
          // Le state global est mis à jour par handleMessage, on recalcule
          calculatePosition();
        }
      } catch {
        // Ignore
      }
    };

    window.addEventListener('message', handleViewportChange);
    // Calcul initial
    calculatePosition();

    return () => {
      window.removeEventListener('message', handleViewportChange);
    };
  }, [isIframe, calculatePosition]);

  // Recalculer quand la ref change (modale montée)
  useEffect(() => {
    if (dialogRef.current) {
      calculatePosition();
    }
  });

  return {
    dialogRef,
    positionTop,
  };
};
